import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, ArrowLeft } from 'lucide-react';



const ManageNews = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [formData, setFormData] = useState({
    title_en: '',
    title_bn: '',
    excerpt_en: '',
    excerpt_bn: '',
    content_en: '',
    content_bn: '',
    category_en: '',
    category_bn: '',
    image_url: '',
    author: '',
    published: true,
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/news/', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) setNews(await response.json());
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const newsData = {
        title: { en: formData.title_en, bn: formData.title_bn },
        excerpt: { en: formData.excerpt_en, bn: formData.excerpt_bn },
        content: { en: formData.content_en, bn: formData.content_bn },
        category: { en: formData.category_en, bn: formData.category_bn },
        image_url: formData.image_url,
        author: formData.author,
        published: formData.published,
      };

      if (editingNews) {
        await fetch(`/api/admin/news/${editingNews._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(newsData),
        });
        toast({ title: 'News updated successfully!' });
      } else {
        await fetch('/api/admin/news', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(newsData),
        });
        toast({ title: 'News created successfully!' });
      }

      setDialogOpen(false);
      resetForm();
      fetchNews();
    } catch (error) {
      toast({ title: 'Error saving news', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this news?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`/api/admin/news/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'News deleted successfully!' });
      fetchNews();
    } catch (error) {
      toast({ title: 'Error deleting news', variant: 'destructive' });
    }
  };

  const handleEdit = (newsItem) => {
    setEditingNews(newsItem);
    setFormData({
      title_en: newsItem.title.en,
      title_bn: newsItem.title.bn,
      excerpt_en: newsItem.excerpt.en,
      excerpt_bn: newsItem.excerpt.bn,
      content_en: newsItem.content.en,
      content_bn: newsItem.content.bn,
      category_en: newsItem.category.en,
      category_bn: newsItem.category.bn,
      image_url: newsItem.image_url,
      author: newsItem.author,
      published: newsItem.published,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingNews(null);
    setFormData({
      title_en: '',
      title_bn: '',
      excerpt_en: '',
      excerpt_bn: '',
      content_en: '',
      content_bn: '',
      category_en: '',
      category_bn: '',
      image_url: '',
      author: '',
      published: true,
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">Manage News</h1>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-party-red hover:bg-party-red-600" onClick={resetForm}>
                <Plus className="h-4 w-4 mr-2" />
                Add News
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingNews ? 'Edit News' : 'Add New News'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Title (English)</Label>
                    <Input
                      value={formData.title_en}
                      onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Title (Bengali)</Label>
                    <Input
                      value={formData.title_bn}
                      onChange={(e) => setFormData({ ...formData, title_bn: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Excerpt (English)</Label>
                    <Textarea
                      value={formData.excerpt_en}
                      onChange={(e) => setFormData({ ...formData, excerpt_en: e.target.value })}
                      required
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Excerpt (Bengali)</Label>
                    <Textarea
                      value={formData.excerpt_bn}
                      onChange={(e) => setFormData({ ...formData, excerpt_bn: e.target.value })}
                      required
                      rows={3}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Content (English)</Label>
                    <Textarea
                      value={formData.content_en}
                      onChange={(e) => setFormData({ ...formData, content_en: e.target.value })}
                      required
                      rows={5}
                    />
                  </div>
                  <div>
                    <Label>Content (Bengali)</Label>
                    <Textarea
                      value={formData.content_bn}
                      onChange={(e) => setFormData({ ...formData, content_bn: e.target.value })}
                      required
                      rows={5}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Category (English)</Label>
                    <Input
                      value={formData.category_en}
                      onChange={(e) => setFormData({ ...formData, category_en: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Category (Bengali)</Label>
                    <Input
                      value={formData.category_bn}
                      onChange={(e) => setFormData({ ...formData, category_bn: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Image URL</Label>
                    <Input
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      required
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <Label>Author</Label>
                    <Input
                      value={formData.author}
                      onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="published"
                    checked={formData.published}
                    onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="published">Publish immediately</Label>
                </div>

                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-party-red hover:bg-party-red-600">
                    {editingNews ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-party-red mx-auto"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {news.map((item) => (
              <Card key={item._id} className="hover:shadow-lg transition-shadow">
                <img src={item.image_url} alt={item.title.en} className="w-full h-48 object-cover" />
                <CardHeader>
                  <CardTitle className="text-lg">{item.title.en}</CardTitle>
                  <p className="text-sm text-gray-500">{item.category.en}</p>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{item.excerpt.en}</p>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-1 rounded ${item.published ? 'bg-party-green-100 text-party-green-700' : 'bg-yellow-100 text-yellow-800'}`}>
                      {item.published ? 'Published' : 'Draft'}
                    </span>
                    <div className="flex space-x-2">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(item)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(item._id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        {!loading && news.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No news articles yet. Click "Add News" to create one.
          </div>
        )}
      </main>
    </div>
  );
};

export default ManageNews;